// @ts-nocheck
import Link from "next/link";

export default function Engines() {
  return (
    <section aria-label="Two intelligence engines: Vio and Nexus" className="eng-sec" id="engines">
      <div className="eng-header">
        <div className="ey rev">Two AI assistants · One platform</div>
        <h2 className="sh2 rev d1">
          One document, <em>read deeply.</em>{" "}<br />
          Every document, <em>searched instantly.</em>
        </h2>
        <p className="eng-sub rev d2">
          <span className="vio-mark">Vio</span> is the copilot inside a single contract — it reads, redlines, drafts, and explains.
          Nexus is the brain across your whole vault — it searches, compares, maps, and warns you.
          Same login, same vault. Different jobs.
        </p>

        {/* Quick contrast strip — fills out the header with concrete differences */}
        <div className="eng-contrast rev d3" role="list">
          <div className="eng-contrast-col" role="listitem">
            <span className="eng-contrast-k">Scope</span>
            <span className="eng-contrast-v"><span className="vio-mark">Vio</span> · one document</span>
            <span className="eng-contrast-v eng-contrast-v-alt">Nexus · every document you own</span>
          </div>
          <div className="eng-contrast-col" role="listitem">
            <span className="eng-contrast-k">Retrieval</span>
            <span className="eng-contrast-v">Clause-level, grounded in source</span>
            <span className="eng-contrast-v eng-contrast-v-alt">Vault-wide, with clause graphs</span>
          </div>
          <div className="eng-contrast-col" role="listitem">
            <span className="eng-contrast-k">Best for</span>
            <span className="eng-contrast-v">Reading, redrafting, explaining</span>
            <span className="eng-contrast-v eng-contrast-v-alt">Comparing, mapping, monitoring</span>
          </div>
        </div>
      </div>

      <div className="eng-grid rev d2">

        {/* ── VIO ───────────────────────────────────────────── */}
        <article className="eng-card eng-vio">
          <header className="eng-card-h">
            <div className="eng-mark">
              <span className="eng-mark-name">Vio<span className="eng-mark-dot">.</span></span>
              <span className="eng-mark-tag">In-document copilot</span>
            </div>
            <p className="eng-card-lead">Reads <em>any one</em> contract — and answers, redrafts, and explains every clause inside it.</p>
          </header>

          <div aria-label="Illustrative Vio assistant analysing a vendor agreement" className="eng-demo eng-demo-vio" role="img">
            <div className="eng-win">
              <div className="eng-win-bar">
                <span className="eng-dot" style={{background:"#FF5F57"}}></span>
                <span className="eng-dot" style={{background:"#FFBD2E"}}></span>
                <span className="eng-dot" style={{background:"#28CA41"}}></span>
                <span className="eng-win-name">VendorAgreement.pdf · Vio</span>
                <span className="eng-win-meta">12 pages · 4,287 words</span>
              </div>

              {/* Document metadata strip — parties + date so it reads like a real file */}
              <div className="eng-doc-meta">
                <span><b>Parties</b> Acme Corp <span className="eng-doc-arrow">↔</span> Northwind Vendors Ltd.</span>
                <span><b>Executed</b> 12 Mar 2024</span>
                <span><b>Term</b> 36 months · auto-renew</span>
              </div>

              <div className="eng-win-body eng-win-body-vio">
                <div className="eng-doc">
                  <span aria-hidden="true" className="eng-scanbeam"></span>

                  <div className="eng-doc-h">§14 INDEMNIFICATION</div>
                  <p className="eng-doc-p">The <span className="eng-hl eng-hl-r">Vendor shall indemnify, defend, and hold harmless the Company from any and all claims,</span> without limitation.</p>

                  <div className="eng-doc-h">§8 INTELLECTUAL PROPERTY</div>
                  <p className="eng-doc-p">All work product and <span className="eng-hl eng-hl-a">pre-existing IP used</span> shall be assigned to Company.</p>

                  <div className="eng-doc-h">§22 TERMINATION</div>
                  <p className="eng-doc-p">Either party may terminate <span className="eng-hl eng-hl-a">for convenience on 30 days&apos; notice</span>, provided fees through end of notice are paid.</p>
                </div>

                <div className="eng-chat">
                  <div className="eng-turn eng-turn-u" data-step="user">Redraft §14 with a 12-month cap on liability.</div>

                  <div className="eng-turn eng-turn-a" data-step="vio">
                    <div className="eng-turn-lbl">Vio · suggested redraft</div>
                    Vendor shall indemnify Company from direct claims arising from wilful misconduct or gross negligence, capped at total fees paid in the preceding 12 months.
                    <div className="eng-cite-row">
                      <span className="eng-cite-k">Grounded in</span>
                      <span className="eng-cite-chip">§14 Indemnification</span>
                      <span className="eng-cite-chip">§8 IP Assignment</span>
                    </div>
                  </div>

                  <div className="eng-meta" data-step="meta">
                    <span className="eng-pill eng-pill-r">Critical · §14</span>
                    <span className="eng-pill eng-pill-a">Review · §8</span>
                    <span className="eng-pill eng-pill-a">Review · §22</span>
                    <span className="eng-score">Score <b>72</b><i>/100</i></span>
                  </div>

                  <div className="eng-foot-bar" data-step="foot">
                    <span aria-hidden="true" className="eng-foot-dot"></span>
                    Every answer carries its source · Verify before you rely on it
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ul className="eng-pts">
            <li><span className="eng-tick">✓</span>Cited Q&amp;A grounded in the document you uploaded — every answer links back to a clause</li>
            <li><span className="eng-tick">✓</span>Refusal mode — Vio won&apos;t invent a clause that isn&apos;t in the source</li>
            <li><span className="eng-tick">✓</span>Three drafting modes: rewrite, counter-position, fallback ladder</li>
            <li><span className="eng-tick">✓</span>Export to .docx with native Word tracked changes — paste straight into negotiation</li>
            <li><span className="eng-tick">✓</span>Turns a sentence into a task draft on the matter — you confirm it; Vio never files one on its own</li>
            <li><span className="eng-tick">✓</span>Speak a request and it becomes a drafting brief — Vio asks for what is missing and never assumes a governing law, a place or a currency you did not say</li>
            <li><span className="eng-tick">✓</span>An answer keeps writing when you leave the page — come back to it, or Stop, Continue and Retry</li>
          </ul>

          <Link className="eng-cta" href="/features#vio"><span>See <span className="vio-mark">Vio</span> in depth →</span></Link>
        </article>

        {/* ── NEXUS ─────────────────────────────────────────── */}
        <article className="eng-card eng-nexus">
          <header className="eng-card-h">
            <div className="eng-mark">
              <span className="eng-mark-name">Nexus<span className="eng-mark-dot">.</span></span>
              <span className="eng-mark-tag">Portfolio intelligence</span>
            </div>
            <p className="eng-card-lead">Thinks across <em>all of them</em> — search, rollups, clause coverage, and a knowledge graph over every document in your vault.</p>
          </header>

          <div aria-label="Illustrative Nexus portfolio search across 23 vendor contracts" className="eng-demo eng-demo-nexus" role="img">
            <div className="eng-win">
              <div className="eng-win-bar">
                <span className="eng-dot" style={{background:"#FF5F57"}}></span>
                <span className="eng-dot" style={{background:"#FFBD2E"}}></span>
                <span className="eng-dot" style={{background:"#28CA41"}}></span>
                <span className="eng-win-name">Nexus · vault search</span>
                <span className="eng-win-meta">238 contracts · last sync 2 min ago</span>
              </div>

              {/* Proactive insight banner — what Nexus is shouting about today */}
              <div className="eng-insight">
                <span className="eng-insight-tag">Insight</span>
                <span className="eng-insight-txt">3 vendor MSAs renewed last week have <b>uncapped liability</b> — diverges from your standard.</span>
              </div>

              <div className="eng-win-body eng-win-body-nexus">
                <div className="eng-search" data-step="search">
                  <span aria-hidden="true" className="eng-search-ico">⌕</span>
                  <span className="eng-search-q">What&apos;s our standard liability cap on vendor MSAs?<span aria-hidden="true" className="eng-caret"></span></span>
                </div>
                <div className="eng-tabs">
                  <span className="eng-tab eng-tab-active">Ask</span>
                  <span className="eng-tab">Search</span>
                  <span className="eng-tab">Clauses</span>
                  <span className="eng-tab">Negotiations</span>
                  <span className="eng-tab">Graph</span>
                  <span className="eng-tab">Insights</span>
                </div>
                <div className="eng-results">
                  <div className="eng-result" data-step="r1">
                    <div className="eng-result-t">Acme MSA <span className="eng-badge">12-mo fees</span></div>
                    <div className="eng-result-m">§ Limitation of Liability · cap = fees paid prior 12 mo · standard</div>
                  </div>
                  <div className="eng-result" data-step="r2">
                    <div className="eng-result-t">Beta SOW <span className="eng-badge">6-mo fees</span></div>
                    <div className="eng-result-m">§ Liability · cap = fees paid prior 6 mo · non-standard</div>
                  </div>
                  <div className="eng-result eng-result-warn" data-step="r3">
                    <div className="eng-result-t">Globex Vendor <span className="eng-badge eng-badge-warn">No cap</span></div>
                    <div className="eng-result-m">§ Indemnification · uncapped · flagged for review</div>
                  </div>
                  <div className="eng-result" data-step="r4">
                    <div className="eng-result-t">Initech MSA v3 <span className="eng-badge">12-mo fees</span></div>
                    <div className="eng-result-m">§ Liability · cap = fees paid prior 12 mo · standard · signed 4 days ago</div>
                  </div>
                </div>

                {/* Mini distribution — visual rollup of how the portfolio splits */}
                <div aria-label="Distribution of liability caps across 23 vendor MSAs" className="eng-dist" data-step="dist">
                  <div className="eng-dist-row">
                    <span className="eng-dist-lbl">12-mo cap</span>
                    <span className="eng-dist-bar"><span className="eng-dist-fill" style={{width:"67%"}}></span></span>
                    <span className="eng-dist-pct">67%</span>
                  </div>
                  <div className="eng-dist-row">
                    <span className="eng-dist-lbl">6-mo cap</span>
                    <span className="eng-dist-bar"><span className="eng-dist-fill" style={{width:"22%"}}></span></span>
                    <span className="eng-dist-pct">22%</span>
                  </div>
                  <div className="eng-dist-row eng-dist-row-warn">
                    <span className="eng-dist-lbl">Uncapped</span>
                    <span className="eng-dist-bar"><span className="eng-dist-fill eng-dist-fill-warn" style={{width:"11%"}}></span></span>
                    <span className="eng-dist-pct">11%</span>
                  </div>
                </div>

                <div className="eng-rollup" data-step="rollup">
                  <span className="eng-rollup-k">Pattern across 23 MSAs</span>
                  <span className="eng-rollup-v">67% cap at 12-mo · 22% at 6-mo · 11% uncapped</span>
                </div>
              </div>
            </div>
          </div>

          <ul className="eng-pts">
            <li><span className="eng-tick">✓</span>Search &amp; Ask across every contract you&apos;ve ever uploaded — natural language, citations included</li>
            <li><span className="eng-tick">✓</span>Clause-coverage map — instantly see which contracts are missing IP, indemnity, or arbitration</li>
            <li><span className="eng-tick">✓</span>Negotiation rollups &amp; benchmarks — your typical liability cap, your usual payment terms, your outliers</li>
            <li><span className="eng-tick">✓</span>Proactive insights — Nexus flags portfolio drift when new contracts diverge from your standard</li>
            <li><span className="eng-tick">✓</span>Reads your meeting minutes and task list too — and asks before anything is filed</li>
          </ul>

          <Link className="eng-cta" href="/features#nexus"><span>See Nexus in depth →</span></Link>
        </article>

      </div>

      <p className="eng-foot rev d3">
        Use <span className="vio-mark">Vio</span> when you&apos;re reading. Use <strong>Nexus</strong> when you&apos;re comparing.
      </p>

      {/* Local styles for the new elements — additive, no overrides of the
          existing .eng-* classes so the scroll-driven animations on .eng-hl-*,
          .eng-turn-*, data-step="r1..r3" and data-step="rollup" keep working. */}
      <style global jsx>{`
        .eng-win-meta{
          margin-left:auto;
          font-family:var(--font-mono), ui-monospace, monospace;
          font-size:9.5px;letter-spacing:.08em;color:rgba(19,18,16,.42);
        }
        .eng-doc-meta{
          display:flex;flex-wrap:wrap;gap:14px;
          padding:8px 14px;background:var(--b50);
          border-bottom:1px solid var(--b200);
          font-size:10px;letter-spacing:.02em;color:rgba(19,18,16,.6);
        }
        .eng-doc-meta b{font-weight:600;color:rgba(19,18,16,.45);margin-right:5px;letter-spacing:.08em;text-transform:uppercase;font-size:9px}
        .eng-doc-arrow{color:var(--cu);margin:0 4px}
        .eng-vio .eng-win-meta{color:rgba(251,250,246,.4)}
        .eng-vio .eng-doc-meta{background:var(--o900);border-bottom-color:rgba(251,250,246,.06);color:rgba(251,250,246,.62)}
        .eng-vio .eng-doc-meta b{color:rgba(251,250,246,.4)}

        .eng-cite-row{
          margin-top:9px;padding-top:8px;
          border-top:1px dashed rgba(184,110,44,.25);
          display:flex;flex-wrap:wrap;align-items:center;gap:5px;
          font-size:10px;
        }
        .eng-cite-k{
          font-family:var(--font-mono), ui-monospace, monospace;
          font-size:8.5px;letter-spacing:.14em;text-transform:uppercase;
          color:rgba(19,18,16,.5);margin-right:3px;
        }
        .eng-cite-chip{
          padding:2px 7px;border-radius:2px;
          background:rgba(184,110,44,.1);
          border:1px solid rgba(184,110,44,.28);
          color:rgba(120,53,15,.95);
          font-size:9.5px;letter-spacing:.02em;
        }
        .eng-vio .eng-cite-k{color:rgba(251,250,246,.55)}
        .eng-vio .eng-cite-chip{background:rgba(184,110,44,.22);border-color:rgba(184,110,44,.4);color:var(--cl2)}

        .eng-foot-bar{
          display:flex;align-items:center;gap:7px;
          margin-top:6px;padding-top:8px;
          border-top:1px solid var(--b200);
          font-family:var(--font-mono), ui-monospace, monospace;
          font-size:9px;letter-spacing:.1em;
          color:rgba(19,18,16,.5);
        }
        .eng-foot-dot{
          width:6px;height:6px;border-radius:50%;
          background:var(--green);
          box-shadow:0 0 0 3px rgba(22,163,74,.15);
        }
        .eng-vio .eng-foot-bar{border-top-color:rgba(251,250,246,.06);color:rgba(251,250,246,.5)}

        .eng-insight{
          display:flex;align-items:center;gap:10px;
          padding:9px 14px;
          background:rgba(217,119,6,.07);
          border-bottom:1px solid rgba(217,119,6,.18);
          font-size:11px;color:rgba(19,18,16,.78);line-height:1.5;
        }
        .eng-insight-tag{
          font-family:var(--font-mono), ui-monospace, monospace;
          font-size:8.5px;letter-spacing:.16em;text-transform:uppercase;
          padding:2px 7px;border-radius:2px;
          background:rgba(217,119,6,.18);
          color:rgba(120,53,15,.95);
          border:1px solid rgba(217,119,6,.32);
          flex-shrink:0;
        }
        .eng-insight-txt b{color:var(--o900);font-weight:600}

        .eng-dist{
          display:flex;flex-direction:column;gap:4px;
          padding:10px 12px;
          background:var(--b50);
          border:1px solid var(--b200);
          border-radius:4px;
        }
        .eng-dist-row{
          display:grid;grid-template-columns:74px 1fr 38px;
          align-items:center;gap:10px;
          font-size:10px;color:rgba(19,18,16,.7);
        }
        .eng-dist-lbl{
          font-family:var(--font-mono), ui-monospace, monospace;
          font-size:9px;letter-spacing:.08em;color:rgba(19,18,16,.55);
        }
        .eng-dist-bar{
          position:relative;height:6px;border-radius:3px;
          background:var(--b100);
          overflow:hidden;
        }
        .eng-dist-fill{
          position:absolute;inset:0 auto 0 0;height:100%;
          background:linear-gradient(90deg,var(--cu),var(--cl));
          border-radius:3px;
        }
        .eng-dist-fill-warn{background:linear-gradient(90deg,var(--amber),#F59E0B)}
        .eng-dist-pct{
          font-family:var(--font-fraunces), serif;font-size:13px;font-weight:200;
          color:var(--o900);letter-spacing:-.01em;text-align:right;
        }
        .eng-dist-row-warn .eng-dist-lbl{color:rgba(120,53,15,.9)}
        .eng-dist-row-warn .eng-dist-pct{color:rgba(120,53,15,.95)}

        .eng-contrast{
          display:grid;grid-template-columns:repeat(3,minmax(0,1fr));
          gap:14px;max-width:880px;margin:28px auto 0;
        }
        .eng-contrast-col{
          padding:14px 16px;
          background:#fff;
          border:1px solid var(--b200);
          border-radius:6px;
          text-align:left;
          display:flex;flex-direction:column;gap:5px;
        }
        .eng-contrast-k{
          font-family:var(--font-mono), ui-monospace, monospace;
          font-size:9px;letter-spacing:.18em;text-transform:uppercase;
          color:rgba(19,18,16,.45);margin-bottom:3px;
        }
        .eng-contrast-v{font-size:12.5px;color:rgba(19,18,16,.8);line-height:1.5}
        .eng-contrast-v-alt{color:rgba(19,18,16,.6);padding-top:5px;border-top:1px dashed var(--b300)}

        @media (max-width: 780px){
          .eng-doc-meta{font-size:9.5px;gap:10px}
          .eng-win-meta{display:none}
          .eng-contrast{grid-template-columns:1fr}
        }
      `}</style>
    </section>
  );
}
